// File: src/layouts/StandardLayout.jsx

import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  createContext,
} from 'react';

import { useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';

import TopNav from '../components/TopNav';
import SmsConsentPrompt from '../components/SmsConsentPrompt';
import HostDocsGatePage from '../pages/host/HostDocsGatePage';
import { AuthContext } from '../context/AuthContext';

export const EventsContext = createContext();

const SMS_PROMPT_DELAY_MS = 3000;
const MARKET_KEY = 'tribe_selected_market';
const HOST_DOCS_SESSION_KEY = 'tribe_mobile_host_docs_finished';
let finishedHostDocsSession = '';

// Keep this UI decision across route remounts. A different login/user must check
// again; hosting endpoints still enforce the actual agreement requirement.
function getHostDocsSessionKey(userId) {
  if (!userId) return '';
  try {
    const login = localStorage.getItem('sessionId') ||
      localStorage.getItem('accessToken');
    return login ? JSON.stringify([userId, login]) : `memory:${userId}`;
  } catch {
    return `memory:${userId}`;
  }
}

function hasFinishedHostDocsSession(key) {
  if (!key) return false;
  if (finishedHostDocsSession === key) return true;
  if (key.startsWith('memory:')) return false;
  try {
    return sessionStorage.getItem(HOST_DOCS_SESSION_KEY) === key;
  } catch {
    return false;
  }
}

function rememberFinishedHostDocsSession(key) {
  if (!key) return;
  finishedHostDocsSession = key;
  if (key.startsWith('memory:')) return;
  try {
    sessionStorage.setItem(HOST_DOCS_SESSION_KEY, key);
  } catch {
    // The in-memory result still survives client-side route changes.
  }
}

function clearFinishedHostDocsSession() {
  finishedHostDocsSession = '';
  try { sessionStorage.removeItem(HOST_DOCS_SESSION_KEY); } catch {}
}

// Match the mobile navigation's device/viewport detection.
function isMobileHostScreen() {
  if (typeof window === 'undefined') return false;
  const mobileDevice = typeof navigator !== 'undefined' &&
    /android|iphone|ipad|ipod|mobile|tablet/i.test(navigator.userAgent);
  return mobileDevice || window.innerWidth <= 440;
}


const API_URL = (
  process.env.REACT_APP_API_URL || ''
).replace(/\/+$/, '');

function normalizeMarketId(value) {
  const id = String(value?._id ?? value ?? '');

  return /^[a-fA-F0-9]{24}$/.test(id)
    ? id.toLowerCase()
    : '';
}

function readSavedMarket() {
  try {
    const saved = JSON.parse(
      localStorage.getItem(MARKET_KEY) || 'null'
    );

    return {
      marketId: normalizeMarketId(saved?.marketId),
      label: typeof saved?.label === 'string' ? saved.label : '',
    };
  } catch {
    return { marketId: '', label: '' };
  }
}

function rememberMarket(selection) {
  try {
    localStorage.setItem(
      MARKET_KEY,
      JSON.stringify({
        marketId: selection.marketId,
        label: selection.label || '',
      })
    );
  } catch {
    // Browsing still works when storage is unavailable.
  }
}

export default function StandardLayout({
  children,
  deferSmsPrompt = false,
}) {
  const navigate = useNavigate();

  const {
    currentUser,
    setCurrentUser,
  } = useContext(AuthContext);

  const currentUserId = String(currentUser?._id || '');
  const hostDocsSessionKey = getHostDocsSessionKey(currentUserId);
  const [isMobileHost, setIsMobileHost] = useState(isMobileHostScreen);
  const [hostDocsFinishedFor, setHostDocsFinishedFor] = useState('');
  const [hostDocsVisibleFor, setHostDocsVisibleFor] = useState('');

  useEffect(() => {
    const update = () => setIsMobileHost(isMobileHostScreen());
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    update();
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const finishHostDocs = useCallback(() => {
    rememberFinishedHostDocsSession(hostDocsSessionKey);
    setHostDocsFinishedFor(hostDocsSessionKey);
  }, [hostDocsSessionKey]);

  const showHostDocs = useCallback(() => {
    setHostDocsVisibleFor(hostDocsSessionKey);
  }, [hostDocsSessionKey]);

  const leaveHostDocs = useCallback(() => {
    // Back allows normal member browsing. Hosting APIs still enforce acceptance.
    rememberFinishedHostDocsSession(hostDocsSessionKey);
    setHostDocsFinishedFor(hostDocsSessionKey);
    navigate('/events', { replace: true });
  }, [hostDocsSessionKey, navigate]);

  const accountMarketId = normalizeMarketId(
    currentUser?.selectedMarketId
  );
  const accountMarketName =
    currentUser?.selectedMarketId?.name || '';

  // Market browsing
  const [selection, setSelection] = useState({
    userId: '',
    marketId: '',
    label: '',
  });

  const [marketEvents, setMarketEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventsSettledFor, setEventsSettledFor] = useState('');
  const [eventsError, setEventsError] = useState('');
  const [marketSaveError, setMarketSaveError] = useState('');
  const [eventsRetryCount, setEventsRetryCount] = useState(0);

  const initializedUserRef = useRef('');
  const activeUserRef = useRef(currentUserId);
  const selectionRef = useRef(selection);

  // Serialize preference writes so rapid selections finish in order.
  const marketSaveQueueRef = useRef(Promise.resolve());
  const marketSaveVersionRef = useRef(0);

  const selectedMarketId =
    selection.userId === currentUserId
      ? selection.marketId
      : '';

  const selectedMarketLabel =
    selection.userId === currentUserId
      ? selection.label
      : '';

  // An empty selection during auth/market restoration is still loading.
  const isRestoringMarket = Boolean(localStorage.getItem('accessToken')) &&
    (!currentUserId || selection.userId !== currentUserId);
  const eventsRequestKey = JSON.stringify([
    currentUserId, selectedMarketId, eventsRetryCount,
  ]);
  const isLoadingMarketEvents = isRestoringMarket || Boolean(
    currentUserId && selectedMarketId &&
    (isLoadingEvents || eventsSettledFor !== eventsRequestKey)
  );

  useEffect(() => {
    activeUserRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  const persistMarket = useCallback(
    (marketId) => {
      if (!currentUserId || !marketId) return;

      const userId = currentUserId;
      const token = localStorage.getItem('accessToken');
      const version = ++marketSaveVersionRef.current;

      setMarketSaveError('');

      marketSaveQueueRef.current =
        marketSaveQueueRef.current
          .catch(() => {})
          .then(async () => {
            // Skip queued selections superseded by a newer choice.
            if (
              version !== marketSaveVersionRef.current ||
              activeUserRef.current !== userId
            ) {
              return;
            }

            try {
              if (!token) {
                throw new Error('Please log in again.');
              }

              const res = await axios.put(
                `${API_URL}/users/me/market`,
                { marketId },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              if (
                activeUserRef.current !== userId ||
                version !== marketSaveVersionRef.current
              ) {
                return;
              }

              // AuthContext will expose this setter in its update.
              if (typeof setCurrentUser === 'function') {
                setCurrentUser((previous) => {
                  if (String(previous?._id || '') !== userId) {
                    return previous;
                  }

                  return {
                    ...previous,
                    selectedMarketId:
                      res.data?.selectedMarketId || marketId,
                  };
                });
              }
            } catch (err) {
              if (
                activeUserRef.current !== userId ||
                version !== marketSaveVersionRef.current
              ) {
                return;
              }

              console.error('Saving market preference failed:', err);

              setMarketSaveError(
                'Your city changed for browsing, but could not be saved to your account.'
              );
            }
          });
    },
    [currentUserId, setCurrentUser]
  );

  // Restore once per authenticated user.
  // Prefer the account's market over the browser's saved choice.
  useEffect(() => {
    if (!currentUserId) {
      initializedUserRef.current = '';
      marketSaveVersionRef.current += 1;

      setSelection({
        userId: '',
        marketId: '',
        label: '',
      });

      setMarketSaveError('');
      return;
    }

    if (initializedUserRef.current === currentUserId) {
      return;
    }

    initializedUserRef.current = currentUserId;
    marketSaveVersionRef.current += 1;
    setMarketSaveError('');

    const saved = readSavedMarket();

    const restored = {
      userId: currentUserId,
      marketId: accountMarketId || saved.marketId,
      label: accountMarketId
        ? accountMarketName ||
          (saved.marketId === accountMarketId ? saved.label : '')
        : saved.label,
    };

    selectionRef.current = restored;
    setSelection(restored);

    if (restored.marketId) {
      rememberMarket(restored);

      // Carry a guest's saved choice into an account that has
      // not selected a market yet.
      if (!accountMarketId) {
        persistMarket(restored.marketId);
      }
    }
  }, [
    currentUserId,
    accountMarketId,
    accountMarketName,
    persistMarket,
  ]);

  // Fetch only the selected market's events.
  // Ignore responses belonging to a previous selection.
  useEffect(() => {
    let active = true;

    setMarketEvents([]);
    setEventsError('');

    if (!currentUserId || !selectedMarketId) {
      setIsLoadingEvents(false);
      return undefined;
    }

    setIsLoadingEvents(true);

    async function fetchMarketEvents() {
      try {
        const res = await axios.get(
          `${API_URL}/events`,
          {
            params: { marketId: selectedMarketId },
          }
        );

        if (!Array.isArray(res.data)) {
          throw new Error('Unable to load gatherings.');
        }

        if (active) {
          setMarketEvents(res.data);
        }
      } catch (err) {
        if (!active) return;

        console.error('Loading market events failed:', err);

        setEventsError(
          err.response?.data?.message ||
            'Unable to load gatherings for this city.'
        );
      } finally {
        if (active) {
          setEventsSettledFor(eventsRequestKey);
          setIsLoadingEvents(false);
        }
      }
    }

    fetchMarketEvents();

    return () => {
      active = false;
    };
  }, [
    currentUserId,
    selectedMarketId,
    eventsRetryCount,
    eventsRequestKey,
  ]);

  const handleMarketSelected = (picked) => {
    const marketId = normalizeMarketId(picked?.marketId);

    if (!currentUserId || !marketId) return;

    const nextSelection = {
      userId: currentUserId,
      marketId,
      label: picked.label || picked.market?.name || '',
    };

    selectionRef.current = nextSelection;
    setSelection(nextSelection);

    // Clear the previous city's results immediately.
    setMarketEvents([]);
    setEventsError('');
    setIsLoadingEvents(true);

    rememberMarket(nextSelection);
    persistMarket(marketId);

    // Also refresh if the user selects the same market again.
    setEventsRetryCount((count) => count + 1);
  };

  const refreshEvents = useCallback(() => {
    setEventsRetryCount((count) => count + 1);
  }, []);

  // Auth and user data
  const [userAvatarUrl, setUserAvatarUrl] = useState(null);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);

  // Wait before mounting the SMS prompt.
  const [smsPromptReadyFor, setSmsPromptReadyFor] = useState(null);

  useEffect(() => {
    setSmsPromptReadyFor(null);

    if (deferSmsPrompt || !currentUserId) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSmsPromptReadyFor(currentUserId);
    }, SMS_PROMPT_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [deferSmsPrompt, currentUserId]);

  useEffect(() => {
    setUserAvatarUrl(currentUser?.profilePicUrl || null);
  }, [currentUser?.profilePicUrl]);

  useEffect(() => {
    let active = true;

    setNotifications([]);

    if (!currentUserId) {
      return undefined;
    }

    async function fetchNotifications() {
      try {
        const res = await axios.get(
          `${API_URL}/notifications`,
          {
            params: {
              userId: currentUserId,
              limit: 1000,
            },
          }
        );

        if (active) {
          setNotifications(res.data || []);
        }
      } catch (err) {
        console.error('Failed to load notifications =>', err);
      }
    }

    fetchNotifications();

    return () => {
      active = false;
    };
  }, [currentUserId]);

  useEffect(() => {
    const count = notifications.filter(
      (notification) => !notification.isRead
    ).length;

    setUnreadCount(count);
  }, [notifications]);

  // Real-time notifications.
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      navigate('/login');
      return undefined;
    }

    if (!currentUserId) {
      return undefined;
    }

    const socket = io(process.env.REACT_APP_API_URL, {
      auth: {
        token: accessToken,
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 300,
      reconnectionDelayMax: 3000,
      timeout: 8000,
    });

    const onConnect = () => {
      console.log(
        '[StandardLayout] Notifications socket connected',
        socket.id
      );

      let storedAuth = {};

      try {
        storedAuth = JSON.parse(
          localStorage.getItem('tribe_auth') || '{}'
        );
      } catch {
        // Fall back to the stored access token.
      }

      const freshToken =
        storedAuth?.token ||
        localStorage.getItem('accessToken') ||
        accessToken;

      socket.auth = {
        token: freshToken,
      };

      socket.emit('joinUserRoom', currentUserId);
    };

    const onConnectError = (err) => {
      console.error('[StandardLayout] socket error', err);
    };

    const onNotificationCreated = (newNotification) => {
      if (!newNotification?.userId) return;

      if (
        String(newNotification.userId) === currentUserId
      ) {
        setNotifications((previous) => [
          newNotification,
          ...previous,
        ]);
      }
    };

    const onNotificationRemoved = (removedId) => {
      setNotifications((previous) =>
        previous.filter(
          (notification) =>
            String(notification._id) !== String(removedId)
        )
      );
    };

    socket.on('connect', onConnect);
    socket.on('connect_error', onConnectError);
    socket.on('notificationCreated', onNotificationCreated);
    socket.on('notificationRemoved', onNotificationRemoved);

    return () => {
      socket.off('connect', onConnect);
      socket.off('connect_error', onConnectError);
      socket.off('notificationCreated', onNotificationCreated);
      socket.off('notificationRemoved', onNotificationRemoved);
      socket.disconnect();
    };
  }, [navigate, currentUserId]);

  const handleMarkOneAsRead = async (notification) => {
    if (notification.isRead) return;

    try {
      await axios.put(
        `${API_URL}/notifications/${notification._id}`,
        { isRead: true }
      );

      setNotifications((previous) =>
        previous.map((item) =>
          item._id === notification._id
            ? { ...item, isRead: true }
            : item
        )
      );
    } catch (err) {
      console.error('Mark single read error =>', err);
    }
  };

  const handleMarkAllRead = async () => {
    if (!currentUserId) return;

    try {
      await axios.put(
        `${API_URL}/notifications/markAll`,
        undefined,
        {
          params: { userId: currentUserId },
        }
      );

      setNotifications((previous) =>
        previous.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );
    } catch (err) {
      console.error('Mark all read error =>', err);
    }
  };

  const handleLogoClick = () => navigate('/events');
  const handleDiscoverClick = () => navigate('/events');
  const handleFriendsClick = () => navigate('/friends');

  const handleNotificationsClick = () => {
    setShowNotifDropdown((previous) => !previous);
  };

  const handleLogoutClick = () => {
    marketSaveVersionRef.current += 1;
    clearFinishedHostDocsSession();

    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('jwt');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    navigate('/login');
  };

  const visibleMarketEvents =
    selection.userId === currentUserId
      ? marketEvents
      : [];

  const needsHostDocsCheck = Boolean(isMobileHost && currentUser?.isHost && currentUserId &&
      hostDocsFinishedFor !== hostDocsSessionKey &&
      !hasFinishedHostDocsSession(hostDocsSessionKey));
  const hostDocsVisible = needsHostDocsCheck && hostDocsVisibleFor === hostDocsSessionKey;

  return (
    <>
      {needsHostDocsCheck && (
      <HostDocsGatePage
        key={currentUserId}
        onAccepted={finishHostDocs}
        onBack={leaveHostDocs}
        onRequired={showHostDocs}
      />
      )}
      {!hostDocsVisible && (
    <EventsContext.Provider
      value={{
        marketEvents: visibleMarketEvents,
        selectedMarketId,
        selectedMarketLabel,
        isRestoringMarket,
        isLoadingEvents: isLoadingMarketEvents,
        eventsError,
        refreshEvents,
        onMarketSelected: handleMarketSelected,

        // Compatibility until event-list consumers are updated.
        nearbyEvents: visibleMarketEvents,
        searchActive: true,
      }}
    >
      <TopNav
        variant="standard"
        selectedMarketId={selectedMarketId}
        onMarketSelected={handleMarketSelected}
        onLogoClick={handleLogoClick}
        onDiscoverClick={handleDiscoverClick}
        onFriendsClick={handleFriendsClick}
        onNotificationsClick={handleNotificationsClick}
        onLogoutClick={handleLogoutClick}
        currentUser={currentUser}
        isHost={currentUser?.isHost}
        userAvatarUrl={userAvatarUrl}
        notifications={notifications}
        unreadCount={unreadCount}
        showNotifDropdown={showNotifDropdown}
        onMarkAllRead={handleMarkAllRead}
        onMarkOneRead={handleMarkOneAsRead}
      />

      {marketSaveError && (
        <div
          role="alert"
          style={{
            padding: '8px 16px',
            fontSize: 13,
            color: '#B91C1C',
          }}
        >
          {marketSaveError}{' '}
          <button
            type="button"
            onClick={() =>
              persistMarket(selectionRef.current.marketId)
            }
          >
            Retry
          </button>
        </div>
      )}

      {children || <Outlet />}

      {!deferSmsPrompt &&
        Boolean(currentUserId) &&
        smsPromptReadyFor === currentUserId && (
          <SmsConsentPrompt
            key={currentUserId}
            userId={currentUserId}
          />
        )}
    </EventsContext.Provider>
      )}
    </>
  );
}
